import { cx } from '@emotion/css';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { TextField } from '@mui/material';
/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useState } from 'react';
import { Typography } from '../../../electrons';
import { IconButton } from '../../IconButton';
import { Icon } from '../../icons';
import { useStyles } from './Input.styles';
import { InputEndAdornment } from './components/InputEndAdornment';
import { InputStartAdornment } from './components/InputStartAdornment';
function getHTMLInputType(type, showPassword) {
    switch (type) {
        case 'password':
            return showPassword ? 'text' : 'password';
        case 'money':
            return 'number';
        case 'hex':
            return 'text';
        case 'default':
            return 'text';
        default:
            return type;
    }
}
export function Input(props) {
    var _a, _b, _c;
    const [showPassword, setShowPassword] = useState(false);
    const classes = useStyles();
    const { inaccessibleCharacters, onConfirm, onKeyDown, useInnerState = true } = props;
    const [innerStateValue, setInnerStateValue] = useState(props.value);
    const handleConfirmationButtonsChange = useCallback((event) => {
        setInnerStateValue(event.target.value);
    }, []);
    const handleInnerStateChange = useCallback((event) => {
        setInnerStateValue(event.target.value);
        if (props.onChange) {
            props.onChange(event);
        }
    }, [props.onChange]);
    const handleConfirm = useCallback(() => {
        if (onConfirm)
            onConfirm(`${innerStateValue}`);
    }, [onConfirm, innerStateValue]);
    const handleKeyDown = useCallback((event) => {
        if (inaccessibleCharacters && inaccessibleCharacters.includes(event.key)) {
            event.preventDefault();
        }
        if (event.key === 'Enter') {
            handleConfirm();
        }
        if (onKeyDown) {
            onKeyDown(event);
        }
    }, [handleConfirm, inaccessibleCharacters, onKeyDown]);
    const handleCancel = useCallback(() => {
        setInnerStateValue(props.value);
    }, [props.value]);
    const handleClickShowPassword = useCallback(() => {
        setShowPassword((prev) => !prev);
    }, []);
    const onChange = props.withConfirmationButtons
        ? handleConfirmationButtonsChange
        : useInnerState
            ? handleInnerStateChange
            : props.onChange;
    const value = props.withConfirmationButtons || useInnerState ? innerStateValue : props.value;
    const valueToDisplay = props.type === 'money' ? parseFloat(`${value}`).toFixed(2) : value;
    const getHelperText = () => {
        if (props.error) {
            const helperTextValue = typeof props.error === 'boolean' ? props.helperText : props.error;
            if (!helperTextValue) {
                return null;
            }
            return (React.createElement(Typography, { className: classes.helperText, variant: "body2" },
                React.createElement(InfoOutlinedIcon, { className: classes.errorIcon }),
                " ",
                helperTextValue));
        }
        return props.helperText;
    };
    const fullWidth = props.fullWidth || !!props.width || (props.maxWidth !== 'default' && props.maxWidth !== undefined);
    const baseOverrideInputClass = {
        root: classes.inputRoot,
        input: classes.input,
    };
    const overrideInputClass = props.success
        ? Object.assign(Object.assign({}, baseOverrideInputClass), { notchedOutline: classes.success }) : baseOverrideInputClass;
    const valueChanged = props.value !== innerStateValue;
    const idWithSuffix = (suffix) => 
    // We keep the name "FormInput" in IDs for backward compatibility
    props.id ? `FormInput__${props.id}__${suffix}` : undefined;
    const inputId = idWithSuffix('input');
    const confirmButtonId = idWithSuffix('confirm-btn');
    const discardButtonId = idWithSuffix('discard-btn');
    useEffect(() => {
        if (useInnerState) {
            setInnerStateValue(props.value);
        }
    }, [props.value, useInnerState]);
    const endAdornment = props.showPasswordShowIcon ? (React.createElement(InputEndAdornment, { customEndAdornment: React.createElement(IconButton, { id: 'show-password-button', onClick: handleClickShowPassword, size: 'small' }, React.createElement(Icon, { color: 'primary' }, showPassword ? 'visibility_off' : 'visibility')) })) : (React.createElement(InputEndAdornment, { confirmButtonId: confirmButtonId, discardButtonId: discardButtonId, success: props.success, customEndAdornment: props.customEndAdornment, valueChanged: valueChanged, withConfirmationButtons: props.withConfirmationButtons, handleCancel: handleCancel, handleConfirm: handleConfirm }));
    return (React.createElement(TextField
    // eslint-disable-next-line react/jsx-props-no-spreading
    , Object.assign({}, props.textFieldProps, { className: cx(props.className, { fullWidth }), autoComplete: props.autoComplete, multiline: props.multiline, rows: props.rows, value: valueToDisplay, onChange: onChange, onKeyDown: handleKeyDown, helperText: getHelperText(), placeholder: props.placeholder, error: !!props.error, disabled: props.disabled || props.readOnly, autoFocus: props.autoFocus, fullWidth: fullWidth, type: getHTMLInputType(props.type, showPassword), variant: "outlined", size: "small", color: props.color || 'secondary', name: props.name, id: inputId, InputProps: Object.assign({ startAdornment: (React.createElement(InputStartAdornment, { type: props.type, currency: props.currency, customStartAdornment: props.customStartAdornment })), endAdornment: endAdornment, classes: overrideInputClass }, (_a = props.textFieldProps) === null || _a === void 0 ? void 0 : _a.InputProps), InputLabelProps: Object.assign({}, (_b = props.textFieldProps) === null || _b === void 0 ? void 0 : _b.InputLabelProps), inputProps: Object.assign({ 'data-testid': props.testid, step: props.step || '0.01', min: '0' }, (_c = props.textFieldProps) === null || _c === void 0 ? void 0 : _c.inputProps), inputRef: props.inputRef, onBlur: props.onBlur, onFocus: props.onFocus })));
}
//# sourceMappingURL=Input.js.map